// NEW FILE: src/components/OrderEventsModal.js
// Modal component for manually reordering events

import React, { useState, useEffect } from 'react';
import {
  ArrowUp,
  ArrowDown,
  ChevronsUp,
  GripVertical,
  X,
  Save,
  RotateCcw
} from 'lucide-react';

const OrderEventsModal = ({ events, currentOrder, onSave, onClose }) => {
  // Initialize working order from current order or event IDs
  const [workingOrder, setWorkingOrder] = useState(() => {
    if (currentOrder && currentOrder.length > 0) {
      // Use custom order if available
      return currentOrder;
    }
    // Default to current event order
    return events.map(e => e.id);
  });

  // Get ordered events based on working order
  const orderedEvents = workingOrder
    .map(id => events.find(e => e.id === id))
    .filter(Boolean); // Remove any events that no longer exist

  // Move event up in the list
  const moveUp = (index) => {
    if (index > 0) {
      const newOrder = [...workingOrder];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      setWorkingOrder(newOrder);
    }
  };

  // Move event down in the list
  const moveDown = (index) => {
    if (index < workingOrder.length - 1) {
      const newOrder = [...workingOrder];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      setWorkingOrder(newOrder);
    }
  };

  // Move event to top of the list
  const moveToTop = (index) => {
    if (index > 0) {
      const newOrder = [...workingOrder];
      const [item] = newOrder.splice(index, 1);
      newOrder.unshift(item);
      setWorkingOrder(newOrder);
    }
  };

  // Reset to default order (by event order as they appear)
  const resetToDefault = () => {
    setWorkingOrder(events.map(e => e.id));
  };

  // Handle save
  const handleSave = () => {
    onSave(workingOrder);
    onClose();
  };

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.dataset.modalOverlay) {
        onClose();
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [onClose]);

  return (
    <div
      data-modal-overlay
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <GripVertical className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Reorder Events
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={resetToDefault}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Reset to default order"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Close"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Event List */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {orderedEvents.map((event, index) => (
              <div
                key={event.id}
                className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 transition-colors"
              >
                {/* Order number */}
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                    {index + 1}
                  </span>
                </div>

                {/* Event title */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {event.title}
                  </h3>
                  {event.timeRange && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                      {event.timeRange}
                    </p>
                  )}
                </div>

                {/* Move buttons */}
                <div className="flex items-center gap-2">
                  {/* Move to Top button */}
                  <button
                    onClick={() => moveToTop(index)}
                    disabled={index === 0}
                    className="p-1 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                    title="Move to top"
                  >
                    <ChevronsUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </button>

                  {/* Up/Down buttons */}
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                      title="Move up"
                    >
                      <ArrowUp className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    </button>
                    <button
                      onClick={() => moveDown(index)}
                      disabled={index === orderedEvents.length - 1}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                      title="Move down"
                    >
                      <ArrowDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {orderedEvents.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400">No events to reorder</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {orderedEvents.length} event{orderedEvents.length !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
            >
              <Save className="w-4 h-4" />
              Save Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderEventsModal;
